import React from 'react';
import { Tab } from '@headlessui/react';
import { useFormContext, useFormState } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { useVacationCalculations } from './hooks';
import { useVacationBalancePreview } from './hooks/useVacationBalancePreview';
import { resolveScheduleDeadline } from '@/features/Vacations/utils/vacationScheduleDeadline';
import type { VacationRequest } from '@/features/Vacations/models/vacation.types';
import { vacationRequestService } from '@/features/Vacations/services/vacationRequestService';
import { hasNoHistoryLogged, hasHistoryOverEntitlement } from '@/features/Vacations/utils/vacationDataQuality';
import VacationIndicators from './VacationIndicators';
import VacationStatement from './VacationStatement';
import VacationPeriods from './VacationPeriods';
import PaidRestPeriods from './PaidRestPeriods';
import { VacationReceiptsPanel } from '@/features/Vacations/components/VacationReceipts';
import { VacationRequestHistory } from '@/features/Vacations/components/VacationRequestHistory';
import PaidRestFields from './PaidRestFields';

interface VacationFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId?: string;
}

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}

// Componente que usa o hook de férias para cálculos em tempo real
const VacationFieldset: React.FC<VacationFieldsetProps> = ({ context, collaboratorId }) => {
    const { watch } = useFormContext();
    const { isDirty } = useFormState({ name: ['vacationPeriods', 'startDate'] });
    const formData = watch();
    const [requests, setRequests] = React.useState<VacationRequest[]>([]);

    // Preview do saldo com histórico do formulário
    const { balance: previewBalance, isPreview, canPreview, isSyncing } = useVacationBalancePreview(collaboratorId);

    // Buscar solicitações do colaborador
    React.useEffect(() => {
        if (formData.email) {
            vacationRequestService
                .getByCollaboratorEmail(formData.email)
                .then(setRequests)
                .catch(() => setRequests([]));
        }
    }, [formData.email]);

    // Cálculos do cliente (fallback para colaborador novo ou sem documento no servidor)
    const clientCalculations = useVacationCalculations();

    // Usar servidor (com preview) se disponível, senão client
    const useClientCalculations = !previewBalance || !collaboratorId;
    const balance = useClientCalculations ? clientCalculations.balance : previewBalance.balanceDays;
    const expiredCompulsoryDays = useClientCalculations ? clientCalculations.expiredCompulsoryDays : previewBalance.expiredDays;
    const scheduledDays = useClientCalculations ? clientCalculations.scheduledDays : previewBalance.scheduledDays;

    // Resolver o prazo para mostrar no card
    const scheduleDeadline = useClientCalculations
        ? undefined
        : resolveScheduleDeadline(previewBalance, previewBalance.nextDeadline);

    const nextCompulsoryPeriod = useClientCalculations
        ? clientCalculations.nextCompulsoryPeriod
        : scheduleDeadline?.variant === 'upToDate' || scheduleDeadline?.variant === 'allScheduled'
            ? { days: 0, date: scheduleDeadline.nextEntitlementDate }
            : { days: Math.max(0, previewBalance.balanceDays - previewBalance.expiredDays), date: previewBalance.nextDeadline };

    // VacationStatement usa periods + vacationPeriods quando servidor está disponível, senão vacationHistory do client
    const vacationHistory = useClientCalculations ? clientCalculations.vacationHistory : undefined;
    const periods = useClientCalculations ? undefined : previewBalance.periods;
    const vacationPeriods = formData.vacationPeriods;

    // Detecção de problemas de qualidade de dado
    const hasNoHistory = previewBalance ? hasNoHistoryLogged({
        monthsWorked: previewBalance.monthsWorked,
        legacyTakenDays: previewBalance.legacyTakenDays,
        legacyScheduledDays: previewBalance.legacyScheduledDays,
        eligible: previewBalance.eligible,
    }) : false;

    const hasOverEntitlement = previewBalance ? hasHistoryOverEntitlement({
        unallocatedDays: previewBalance.unallocatedDays,
    }) : false;

    // Verificar elegibilidade para controle de férias
    const isEligible = formData.collaboratorType === 'Celetista';
    const isSupplier = formData.collaboratorType === 'Fornecedor';

    const tabs = [
        {
            name: 'Programação de Férias',
            content: <div className="mt-6"><VacationPeriods context={context} /></div>
        },
        {
            name: 'Extrato',
            content: (
                <div className="mt-6">
                    <VacationStatement
                        vacationHistory={vacationHistory}
                        balance={balance}
                        balanceObj={previewBalance}
                        periods={periods}
                        vacationPeriods={vacationPeriods}
                        requests={requests}
                    />
                </div>
            )
        },
        {
            name: 'Histórico',
            content: (
                <div className="mt-6">
                    <VacationRequestHistory
                        requests={requests}
                        vacationPeriods={vacationPeriods}
                    />
                </div>
            )
        },
        {
            name: 'Recibos',
            content: (
                <div className="mt-6">
                    <VacationReceiptsPanel
                        collaboratorId={collaboratorId}
                        collaboratorEmail={formData.email}
                    />
                </div>
            )
        },
    ];

    return (
        <fieldset className="border p-4 rounded-md">
            <legend className="text-sm font-medium px-1 text-gray-400">
                {isSupplier ? 'Descanso Remunerado' : 'Controle de Férias'}
            </legend>
            <div className="pt-2">
                {isSupplier ? (
                    <div className="space-y-6">
                        <PaidRestFields />
                        <PaidRestPeriods context={context} />
                    </div>
                ) : !isEligible ? (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                        <p className="font-bold">Aba não aplicável</p>
                        <p className="text-sm">O controle de férias se aplica apenas a colaboradores com vínculo Celetista.</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {hasNoHistory && (
                            <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
                                <p className="font-bold text-amber-800">Sem histórico lançado</p>
                                <p className="text-sm text-amber-700">Nenhuma férias do histórico foi registrada neste cadastro. O saldo pode estar superestimado. Inclua as férias já gozadas para corrigir o cálculo.</p>
                            </div>
                        )}

                        {hasOverEntitlement && (
                            <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
                                <p className="font-bold text-amber-800">Histórico maior que o direito</p>
                                <p className="text-sm text-amber-700">O histórico de férias lançado é maior que os dias de direito adquirido. Verifique as datas e quantidades.</p>
                            </div>
                        )}

                        {isSyncing && (
                            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                                <p className="text-sm text-blue-700">Atualizando o saldo com o servidor…</p>
                            </div>
                        )}

                        {isPreview && !isSyncing && (
                            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                                <p className="text-sm text-blue-700">Prévia: os números já consideram suas alterações. Salve para confirmar.</p>
                            </div>
                        )}

                        {isDirty && !canPreview && !isPreview && (
                            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                                <p className="text-sm text-blue-700">A data de início mudou. Os números serão recalculados depois de salvar.</p>
                            </div>
                        )}

                        <VacationIndicators
                            balance={balance}
                            expiredCompulsoryDays={expiredCompulsoryDays}
                            nextCompulsoryPeriod={nextCompulsoryPeriod}
                            scheduledDays={scheduledDays}
                            isServerCalculation={!useClientCalculations}
                            deadlineVariant={useClientCalculations ? undefined : (scheduleDeadline?.variant === 'entitledFrom' ? 'deadline' : scheduleDeadline?.variant)}
                        />

                        <Tab.Group>
                            <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1">
                                {tabs.map((tab) => (
                                    <Tab
                                        key={tab.name}
                                        className={({ selected }) =>
                                            classNames(
                                                'w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700',
                                                'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                                                selected
                                                    ? 'bg-white shadow'
                                                    : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
                                            )
                                        }
                                    >
                                        {tab.name}
                                    </Tab>
                                ))}
                            </Tab.List>
                            <Tab.Panels className="mt-2">
                                {tabs.map((tab, idx) => (
                                    <Tab.Panel
                                        key={idx}
                                        className={classNames(
                                            'rounded-xl bg-white p-3',
                                            'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2'
                                        )}
                                    >
                                        {tab.content}
                                    </Tab.Panel>
                                ))}
                            </Tab.Panels>
                        </Tab.Group>
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default VacationFieldset; 